import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Library, Volume2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VocabularyPage = () => {
  const [searchParams] = useSearchParams();
  const [vocabulary, setVocabulary] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [expandedThemes, setExpandedThemes] = useState({});
  const { user } = useAuth();

  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  useEffect(() => {
    fetchThemes();
  }, [selectedLevel]);

  useEffect(() => {
    fetchVocabulary();
  }, [selectedLevel]);

  const fetchThemes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vocabulary/themes?level=${selectedLevel}`);
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
        // Expand first theme by default
        if (data.length > 0) {
          setExpandedThemes({[data[0]]: true});
        }
      }
    } catch (error) {
      console.error('Failed to fetch themes:', error);
    }
  };

  const fetchVocabulary = async () => {
    try {
      let url = `${BACKEND_URL}/api/vocabulary?level=${selectedLevel}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setVocabulary(data);
      }
    } catch (error) {
      console.error('Failed to fetch vocabulary:', error);
    } finally {
      setLoading(false);
    }
  };

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      
      const voices = window.speechSynthesis.getVoices();
      const germanVoice = voices.find(voice => 
        voice.lang.startsWith('de') && (voice.name.includes('Google') || voice.name.includes('Microsoft'))
      ) || voices.find(voice => voice.lang.startsWith('de'));
      
      if (germanVoice) {
        utterance.voice = germanVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleTheme = (theme) => {
    setExpandedThemes(prev => ({
      ...prev,
      [theme]: !prev[theme]
    }));
  };

  const getVocabByTheme = (theme) => {
    return vocabulary.filter(v => v.theme === theme);
  };

  const isPremium = user?.subscription_status === 'premium';

  return (
    <div className="min-h-screen bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white flex flex-col">
      <Navbar />

      <main className="flex-1 py-8 px-6">
        <div className="container mx-auto">
          <div className="mb-8 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-2">Vocabulary by Theme</h1>
            <p className="text-babyblue-600">Build your German vocabulary with organized themed word lists</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-babyblue-600 mb-2 text-center">Select Level</label>
            <div className="flex flex-wrap gap-2 justify-center">
              {levels.map((level) => (
                <button
                  key={level}
                  onClick={() => setSelectedLevel(level)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedLevel === level 
                      ? 'bg-primary text-white shadow-lg shadow-primary/50' 
                      : 'bg-white text-babyblue-600 hover:bg-white border border-primary/30'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {themes.map((theme) => {
                const themeVocab = getVocabByTheme(theme);
                const isExpanded = expandedThemes[theme];
                
                return (
                  <div key={theme} className="card">
                    <button
                      onClick={() => toggleTheme(theme)}
                      className="w-full flex items-center justify-between p-4 hover:bg-white rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Library className="w-6 h-6 text-primary" />
                        <h2 className="font-heading text-2xl font-semibold text-babyblue-800">{theme}</h2>
                        <span className="text-sm text-babyblue-500">({themeVocab.length} words)</span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6 text-primary" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-babyblue-500" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4">
                        {themeVocab.map((word) => (
                          <div key={word.vocab_id} className="bg-gradient-to-br from-[#F5F0E8] to-[#FFF8F0] rounded-lg p-6 border-2 border-[#E8DCC8] hover:border-primary/50 transition-all hover:shadow-lg hover:shadow-primary/20 cursor-pointer" onClick={() => speak(word.german_word)} data-testid={`vocab-card-${word.vocab_id}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-heading text-xl font-semibold text-babyblue-900">{word.german_word}</h3>
                                  <button
                                    type="button"
                                    className="p-1 hover:bg-primary/10 rounded-full transition-colors"
                                    title="Pronounce"
                                  >
                                    <Volume2 className="w-4 h-4 text-primary" />
                                  </button>
                                </div>
                                <p className="text-babyblue-600 font-medium">{word.english_translation}</p>
                              </div>
                              <span className="level-badge">{word.level}</span>
                            </div>
                            <p className="text-sm text-babyblue-500 italic leading-relaxed">{word.example_sentence}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Ad Revenue Space */}
          <div className="ad-container mt-8" data-testid="ad-placeholder">
            <p>Advertisement Space</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VocabularyPage;
